import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";

import { SpinnerFullScreen } from "../../components/SpinnerFullScreen";
import { SubHeader } from "../../components/SubHeader";
import { DashboardMode } from "../../model/dashboard/types/DashboardMode.enum";
import { dashboardSliceSelectors } from "../../redux/dashboard/dashboard.selector";
import { dashboardActions } from "../../redux/dashboard/dashboard.slice";
import { formActions } from "../../redux/forms/form.slice";
import { permissionActions } from "../../redux/permission/permission.slice";
import { settingsSliceSelectors } from "../../redux/settings/settings.selector";
import { settingsActions } from "../../redux/settings/settings.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { Breakpoints } from "../../theme/Breakpoints";
import { Spacing } from "../../theme/Spacing";
import { AtiraToast } from "../../utils/AtiraToast";
import { PageIcons } from "../../utils/GlobalIcons";
import { resolveDashboardMode } from "./DashboardUtils";
import { DashboardModeToggle } from "./components/DashboardModeToggle";
import { DashboardSetupBoard } from "./components/setup/DashboardSetupBoard";
import { DashboardToday } from "./components/today/DashboardToday";

const Wrapper = styled.div`
  width: 100%;
  padding: 0 ${Spacing.m} ${Spacing.l};
`;

// The sub-header has a fixed height, so on phones the toggle moves into the
// page instead of squeezing the title
const HeaderToggle = styled.div`
  @media (max-width: ${Breakpoints.PHONE}) {
    display: none;
  }
`;

const PhoneToggle = styled.div`
  display: none;
  margin-bottom: ${Spacing.m};

  @media (max-width: ${Breakpoints.PHONE}) {
    display: block;
  }
`;

const Dashboard: React.FC = () => {
  const [pickedMode, setPickedMode] = useState<DashboardMode | null>(null);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const isMember = Boolean(
    useAppSelector(userSliceSelectors.selectIsUserOrgMember),
  );
  const settings = useAppSelector(settingsSliceSelectors.selectSettings);
  const setup = useAppSelector(dashboardSliceSelectors.selectSetup);
  const dataVersion = useAppSelector(dashboardSliceSelectors.selectDataVersion);

  const mode = resolveDashboardMode({
    isMember,
    picked: pickedMode || settings.dashboardMode,
    setup,
  });

  const onChangeMode = async (next: DashboardMode) => {
    setPickedMode(next);

    try {
      await dispatch(
        settingsActions.updateSettings({ userId, dashboardMode: next }),
      ).unwrap();
      await dispatch(settingsActions.getSettings({ userId })).unwrap();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  useEffect(() => {
    dispatch(formActions.getForms({ userId }));
    dispatch(settingsActions.getSettings({ userId }));
  }, [dispatch, userId]);

  useEffect(() => {
    if (!isMember) {
      dispatch(dashboardActions.getSetup({ userId }));
    }
  }, [dispatch, userId, isMember, dataVersion]);

  useEffect(() => {
    dispatch(dashboardActions.getToday({ userId }));
  }, [dispatch, userId, dataVersion]);

  useEffect(() => {
    const inviteMode = searchParams.get("inviteMode");
    const inviteCode = searchParams.get("inviteCode");
    if (inviteMode && inviteCode) {
      dispatch(permissionActions.getInviteByCode({ inviteCode, userId }));
    }
  }, [userId, dispatch, searchParams]);

  return (
    <div>
      <SubHeader
        title={t("subheader.dashboard")}
        icon={PageIcons.Dashboard}
        extra={
          !isMember && mode ? (
            <HeaderToggle>
              <DashboardModeToggle mode={mode} onChange={onChangeMode} />
            </HeaderToggle>
          ) : null
        }
      />

      <Wrapper>
        {!isMember && mode ? (
          <PhoneToggle>
            <DashboardModeToggle
              mode={mode}
              onChange={onChangeMode}
              block
              withTestIds={false}
            />
          </PhoneToggle>
        ) : null}
        {mode === DashboardMode.SETUP && setup ? (
          <DashboardSetupBoard
            setup={setup}
            onSwitchToToday={() => onChangeMode(DashboardMode.TODAY)}
          />
        ) : mode === DashboardMode.TODAY ? (
          <DashboardToday />
        ) : (
          <SpinnerFullScreen />
        )}
      </Wrapper>
    </div>
  );
};

export default Dashboard;
